import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  IconButton,
  Avatar,
  SxProps,
  ButtonProps,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { LoadingButton } from '@mui/lab';
import {
  EMOJI_RATINGS,
  FEEDBACK_QUESTIONS,
  TP_COLORS,
} from '../../../constants';
import { TPTextField } from '../../Inputs';
import {
  createLessonStudentFeedback,
  resetPendingLessonStudentFeedback,
  TCreateLessonStudentFeedback,
} from '../../../store/V2/LessonStudentFeedbacksSlice';
import { ILessonHistory } from '../../../store/V2/LessonHistorySlice';
import { ISection } from '../../../store/V2/SectionSlice';
import { setFeedbackModalCancelledToLocalStorage } from '../../../helper/LocalStorage';

interface ClassFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  classData: ILessonHistory;
}

interface IState {
  tutorExplanationClarity: number;
  tutorSupportResponsiveness: number;
  tutorAttitudeFriendliness: number;
  classInteractionEngagement: number;
  studentConfidenceImprovement: number;
  comments?: string;
}

const modalStyles: SxProps = {
  '& .MuiDialog-paper': {
    borderRadius: '30px',
    maxWidth: '540px',
  },
};

const headerStyles: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  padding: '20px',
};

const contentStyles: SxProps = {
  padding: '16px 32px',
  backgroundColor: '#FFFFFF',
  maxHeight: '70vh',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#F1F5F9',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#CBD5E1',
    borderRadius: '3px',
  },
};

const emojiContainerStyles: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
};

const iconButtonStyles: SxProps = {
  width: { xs: '45px', sm: '55px' },
  height: { xs: '45px', sm: '55px' },
  color: 'unset',
};

const cancelButtonProps: ButtonProps = {
  variant: 'outlined',
  sx: {
    borderColor: '#D1D5DB',
    color: '#374151',
    fontWeight: 600,
    padding: '12px 24px',
    borderRadius: '8px',
    textTransform: 'none',
    fontSize: '14px',
    '&:hover': {
      borderColor: '#9CA3AF',
      backgroundColor: '#F9FAFB',
    },
  },
};

const submitButtonProps: ButtonProps = {
  variant: 'contained',
  color: 'secondary',
  sx: {
    fontSize: '14px',
    fontWeight: 600,
    padding: '12px 24px',
    borderRadius: '8px',
    backgroundColor: TP_COLORS.gray.darken,
  },
};

const ClassFeedbackModal: React.FC<ClassFeedbackModalProps> = ({
  open,
  onClose,
  classData,
}) => {
  const dispatch = useDispatch();

  const [state, setState] = useState<IState>({
    tutorExplanationClarity: 0,
    tutorSupportResponsiveness: 0,
    classInteractionEngagement: 0,
    tutorAttitudeFriendliness: 0,
    studentConfidenceImprovement: 0,
    comments: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tutorData =
    classData.tutors && classData.tutors.length
      ? classData.tutors[0]
      : undefined;

  const handleRatingChange = (questionId: keyof IState, value: number) => {
    setState((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleCommentsChange = (comments: string) => {
    setState((prev) => ({
      ...prev,
      comments,
    }));
  };

  const isFormValid = () => {
    return (
      state.tutorExplanationClarity > 0 &&
      state.tutorSupportResponsiveness > 0 &&
      state.tutorAttitudeFriendliness > 0 &&
      state.classInteractionEngagement > 0 &&
      state.studentConfidenceImprovement > 0
    );
  };

  const handleCancel = () => {
    setFeedbackModalCancelledToLocalStorage(classData._id);
    onClose();
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setIsSubmitting(true);

    const apiData: TCreateLessonStudentFeedback = {
      tutors: classData.tutors.map((item) => item._id),
      section: (classData.section as ISection)._id,
      tutorExplanationClarity: state.tutorExplanationClarity,
      tutorSupportResponsiveness: state.tutorSupportResponsiveness,
      tutorAttitudeFriendliness: state.tutorAttitudeFriendliness,
      classInteractionEngagement: state.classInteractionEngagement,
      studentConfidenceImprovement: state.studentConfidenceImprovement,
      feedbackComments: state.comments,
    };

    const res: any = await dispatch(createLessonStudentFeedback(apiData));

    if (res.payload) {
      dispatch(resetPendingLessonStudentFeedback());
      onClose();
    }

    setIsSubmitting(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={modalStyles}
    >
      <Box sx={headerStyles}>
        <Avatar
          src={tutorData?.avatar}
          alt={tutorData?.firstname}
          sx={{ width: 48, height: 48 }}
        />

        <Box sx={{ flex: 1 }}>
          <Typography fontSize="18px" fontWeight="600">
            How was your class with {tutorData?.firstname || ''}{' '}
            {tutorData?.lastname || ''}
          </Typography>
          <Typography fontSize="14px" color="#647488">
            {moment(classData.start).format('dddd, MMMM D : h:mm A')}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={contentStyles}>
        <Stack spacing={4}>
          {FEEDBACK_QUESTIONS.map((question) => (
            <Stack key={question.id} spacing={2}>
              <Typography fontSize="14px" fontWeight="600">
                {question.question}
              </Typography>
              <Box sx={emojiContainerStyles}>
                {EMOJI_RATINGS.map((rating) => (
                  <IconButton
                    key={rating.value}
                    onClick={() =>
                      handleRatingChange(
                        question.id as keyof IState,
                        rating.value
                      )
                    }
                    sx={{
                      ...iconButtonStyles,
                      ...(state[question.id as keyof IState] === rating.value
                        ? {
                            backgroundColor: TP_COLORS.gray.light,
                            '&:hover': {
                              backgroundColor: TP_COLORS.gray.darken,
                            },
                          }
                        : {
                            backgroundColor: TP_COLORS.listItemGray.light,
                          }),
                    }}
                  >
                    {rating.emoji}
                  </IconButton>
                ))}
              </Box>
            </Stack>
          ))}

          <Stack spacing={2}>
            <Typography fontSize="14px" fontWeight="600">
              6. What’s one thing that could make your classes more helpful?
            </Typography>
            <TPTextField
              fullWidth
              multiline
              minRows={5}
              value={state.comments}
              onChange={(e) => handleCommentsChange(e.target.value)}
              placeholder="Type here ..."
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '20px',
          gap: 2,
        }}
      >
        <Button {...cancelButtonProps} onClick={handleCancel}>
          Cancel
        </Button>
        <LoadingButton
          {...submitButtonProps}
          onClick={handleSubmit}
          disabled={!isFormValid() || isSubmitting}
          loading={isSubmitting}
        >
          Submit Feedback
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ClassFeedbackModal;
